/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import { FrameSection, FrameItem } from '@ccxi/ccxi-shared-components/src/HeaderFooterBase';
import Header from '@ccxi/ccxi-shared-components/src/Header';
import HeaderNameComponent from '@ccxi/ccxi-shared-components/src/HeaderNameComponent';
import Link from '@react/react-spectrum/Link';
import Carousel from '@ccxi/ccxi-shared-components/src/Carousel';
import Icons from '../../../../utils/iconMapping';
import { PDPAnalytics } from '../../../../analytics';
import { PDP_ACTION, PDP_SECTION } from '../../../../utils/pdpUtils/pdpEnums';
import ContentParser from '../../../../utils/contentParser';
import TranslationUtil from '../../../../utils/translation';


const ACTION_HANDLER_TYPES = {
    CARD_CLICK: 'card-click',
    VIEW_MORE_CLICK: 'view-more-click',
};

class BehanceGallery extends Component {
    constructor(props) {
        super(props);
        this.carouselCardMap = {
            L: {
                breakpointL: {
                    gutter: 16,
                    cardsPerRow: 3,
                },
                breakpointLM: {
                    gutter: 16,
                    cardsPerRow: 3,
                },
                breakpointM: {
                    gutter: 16,
                    cardsPerRow: 3,
                },
                breakpointMS: {
                    gutter: 16,
                    cardsPerRow: 2,
                },
                breakpointS: {
                    gutter: 12,
                    cardsPerRow: 3,
                },
                breakpointSS: {
                    gutter: 12,
                    cardsPerRow: 2,
                },
                breakpointXS: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
            },
        };
    }

    componentDidMount() {
        if (this.sectionData) {
            const { cardList, sectionId } = this.sectionData;
            cardList.forEach((card) => {
                PDPAnalytics.renderBehanceProjects(card, sectionId);
            });
        }
    }

    get sectionData() {
        const { PDPDataObject } = this.props;
        return this.validateAndProcessData(PDPDataObject);
    }

    validateBehanceItem(behanceItem, localAssetsBasePath) {
	    return (behanceItem.name && behanceItem.image
            && behanceItem.image && ContentParser
            .getAssetPath(behanceItem.image, localAssetsBasePath));
    }

    validateAndProcessData({ contentfulData, localAssetsBasePath }) {
        if (!contentfulData.behanceList
			|| !contentfulData.behanceList.length
			|| !contentfulData.behanceTitle) {
	        return null;
	    }
	    const behanceListModel = [];
	    contentfulData.behanceList.forEach((value) => {
	        // eslint-disable-next-line
	        this.validateBehanceItem(value,localAssetsBasePath) &&
	            behanceListModel.push({
	                actionId: PDP_ACTION.CARD_CLICK,
	                imageURL: ContentParser.getAssetPath(value.image, localAssetsBasePath),
	                cardName: value.name,
	                cardAuthor: value.creator,
	                cardURL: value.link && value.link.url,
	                urlData: value.link,
	                renderGuid: uuid.v4(),
	            });
	    });

	    const behanceModel = {
	        sectionId: PDP_SECTION.BEHANCE_GALLERY,
	        sectionName: contentfulData.behanceTitle, // 'Made with Lightroom'
	        viewMore: {},
            cardList: behanceListModel,
	    };
	    if (contentfulData.moreBehanceLink && contentfulData.moreBehanceLink.url) {
	        behanceModel.viewMore = {
	            actionId: PDP_ACTION.VIEW_MORE_CLICK,
	            url: contentfulData.moreBehanceLink.url,
	            urlData: contentfulData.moreBehanceLink,
	        };
	    }
	    return behanceModel;
    }

    actionHandler(e, type, ...args) {
        const { PDPDataObject: { actionHandlerCallback } } = this.props;
        const guid = uuid.v4();
        let actionParam;
        switch (type) {
        case ACTION_HANDLER_TYPES.CARD_CLICK:
            const [card] = args;
            actionParam = {
                actionObject: card,
                guid,
            };
            actionHandlerCallback(
                e, this.sectionData.sectionId, card.actionId, actionParam
            );

            PDPAnalytics.clickBehanceProject(card, guid, this.sectionData.sectionId);

            break;
        case ACTION_HANDLER_TYPES.VIEW_MORE_CLICK:
            const { viewMore } = this.sectionData;
            actionParam = {
                actionObject: viewMore,
                guid,
            };
            actionHandlerCallback(
                e, this.sectionData.sectionId, viewMore.actionId, actionParam
            );

            PDPAnalytics.viewMoreBehanceClick(viewMore, guid, this.sectionData.sectionId);
            break;
        default:
        }
    }

    renderSectionHeader() {
        const { LinkOut } = Icons;
        const { viewMore } = this.sectionData;
        return (
            <Header className="behance-gallery-header">
                <FrameSection position="left" className="behance-gallery-header-left">
                    <FrameItem>
                        <HeaderNameComponent headerName={this.sectionData.sectionName} />
                    </FrameItem>
                </FrameSection>
                {viewMore && viewMore.actionId && (
                    <FrameSection position="right" tabIndex={0} role="link" aria-label="View More" className="behance-gallery-header-right" onKeyDown={(e) => { if (e.keyCode === 13) { this.actionHandler(e, ACTION_HANDLER_TYPES.VIEW_MORE_CLICK); } }} onClick={(e) => { this.actionHandler(e, ACTION_HANDLER_TYPES.VIEW_MORE_CLICK); }}>
                        <FrameItem className="view-more">
                            <Link>
                                {TranslationUtil.getMessage('ccx.pdp.view-more')}
                            </Link>
                        </FrameItem>
                        <FrameItem className="link-out-icon">
                            <LinkOut />
                        </FrameItem>
                    </FrameSection>
                )}
            </Header>
        );
    }

    renderCards(cardsList) {
        const cardList = [];
        cardsList.forEach((card) => {
            cardList.push(
                <div
                    className="behance-gallery-card"
                    tabIndex={0}
                    render-guid={card.renderGuid}
                    role="link"
                    aria-label={`${card.cardName}, ${card.cardAuthor}`}
                    key={`behance-card-${card.cardName}`}
                    onKeyDown={(e) => {
                        if (e.keyCode === 13) {
                            this.actionHandler(e, ACTION_HANDLER_TYPES.CARD_CLICK, card);
                        }
                    }}
                    onClick={(e) => {
                        this.actionHandler(e, ACTION_HANDLER_TYPES.CARD_CLICK, card);
                    }}>
                    <img src={card.imageURL} alt="" />
                    <div className="card-detail">
                        <div className="card-name">
                            {card.cardName}
                        </div>
                        <div className="card-author">
                            {card.cardAuthor}
                        </div>
                    </div>
                </div>
            );
        });
        return cardList;
    }

    render() {
        return this.sectionData && (
            <div className="behance-gallery left-panel-compt" style={{ margin: 'auto' }}>
                { this.renderSectionHeader() }
                <div className="behance-gallery-carousel">
                    <Carousel cardSize="L" customCardMapping={this.carouselCardMap} actionEvents={PDPAnalytics.carouselButtonClick}>
                        { this.renderCards(this.sectionData.cardList) }
                    </Carousel>
                </div>
            </div>
        );
    }
}

export default observer(BehanceGallery);
